import { getApiRoot } from '@/helpers/api';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import apiClient from '@plugins/api-client';
import { getKeycloak } from '@/helpers/keycloak';
import { useApi } from '@/composables/useApi';
import { useI18n } from 'vue-i18n';
import { useSnackbarStore } from '@/store/snackbar';

/** @typedef {import('@/types/offer').OfferFile} OfferFile */
/** @typedef {import('@/types/offer').OfferFileCategory} OfferFileCategory */

export const OfferTypes = {
  SolarPanels: 1,
  SolarBoilers: 2,
  HeatPumps: 3,
  HeatPumpBoiler: 4,
  Other: 5,
  Batteries: 6,
  ChargingStations: 10,
  Control: 15,
  Service: 25,
  EMS: 35,
  FloorHeating: 50,
  Repairs: 51,
};

export const OfferTypesIcons = {
  default: 'mdi-progress-wrench',
  solarPanels: '$energy',
  solarBoilers: 'mdi-sun-thermometer-outline',
  airToAirHeatPumps: 'mdi-heat-pump-outline',
  heatPumpBoiler: 'mdi-water-boiler',
  batteries: 'mdi-battery-outline',
  chargingStations: 'mdi-ev-station',
  floorHeating: 'mdi-heating-coil',
  ems: 'mdi-home-lightning-bolt-outline',
  repairs: 'mdi-wrench-outline',
};

export const useOfferStore = defineStore('offerStore', () => {
  const { t } = useI18n();
  const { apiGet, apiPost } = useApi();

  const snackbarStore = useSnackbarStore();

  const offer = ref(/** @type {Record<string, any> | null} */ (null));
  const versions = ref(null);
  const offerFiles = ref(/** @type {OfferFile[]} */ ([]));
  const offerFileCategories = ref(/** @type {OfferFileCategory[]} */ ([]));

  const offerIsWithdrawn = computed(() => Boolean(offer.value?.withdrawnViaPortalTimestamp));
  const offerDocument = computed(() => getOfferDocument(offer.value));

  async function getOfferByID(id, versionNumber) {
    if (offer.value?.offerId !== Number(id)) {
      reset();
    }
    offer.value = await apiGet(`offers/${id}/versions/${versionNumber}`);
  }

  async function getOfferVersions(id) {
    const headers = { Authorization: `Bearer ${(await getKeycloak()).token}` };
    const response = await apiClient.get(
      `${getApiRoot()}/offers/${id}/versions`,
      { headers },
    );
    if (response.data.errors.length === 0) {
      versions.value = response.data.data;
    } else {
      throw new Error(response.data.errors);
    }
  }

  async function getOfferAttachments(id, versionNumber) {
    return apiGet(`offers/${id}/versions/${versionNumber}/attachments`);
  }

  function signOffer(offerId, versionId, base64image) {
    return apiPost(
      `offers/${offerId}/sign`,
      {
        version: versionId,
        file: base64image,
      },
    );
  }

  async function storeUpsell(type, name, file) {
    return await apiPost(`offers/${offer.value.offerId}/upsale`, {
      versionNumber: offer.value.versionNumber,
      type,
      name,
      file,
    });
  }

  async function submitCustomerView(offerId, versionNumber) {
    return await apiPost(`offers/${offerId}/seen`, { versionNumber });
  }

  async function submitWithdrawal() {
    if (!offer.value?.offerId) {
      return;
    }
    const result = await apiPost(`offers/${offer.value.offerId}/withdrawal`, { versionNumber: offer.value.versionNumber });

    if (!result[0]?.result) {
      snackbarStore.showError(t('genericServerError'));
    }

    await getOfferByID(offer.value.offerId, offer.value.versionNumber);
  }

  function getOfferDocument(offer) {
    return {
      name: 'offer',
      downloading: false,
      cdn: false,
      text: t('offerDetail.offer'),
      downloadName: `${t('offerDetail.offer')}-${offer.offerId}-v${offer.versionNumber}.pdf`,
      url: `offers/${offer.offerId}/files/${offer.offerTypeId}?type=print&versionNumber=${offer.versionNumber}`,
    };
  }

  async function getOfferFiles(id, version) {
    offerFiles.value = await apiGet(`offers/${id}/versions/${version}/files`) ?? [];
  }

  async function getOfferFileCategories(id) {
    offerFileCategories.value = await apiGet(`offers/${id}/file-categories`) ?? [];
  }

  /**
   * Saves the offer files in one request: the files that were picked locally are uploaded
   * and the saved files in `formData.filesToDelete` are deleted.
   * The api applies both or neither: on success uploaded files are swapped for the stored ones in `formData`
   * and `formData.filesToDelete` is emptied; on failure everything is kept so submit can retry.
   *
   * @param {number | string} id
   * @param {number | string} version
   * @param {{ files: Record<number, OfferFile[]>; filesToDelete: OfferFile[] }} formData files per category id, and the saved files to delete
   * @returns {Promise<boolean>} true when the files were saved
   */
  async function postOfferFiles(id, version, formData) {
    const pending = Object.entries(formData.files)
      .flatMap(([categoryId, files]) => files
        .filter((file) => file.pendingFile)
        .map((file) => ({ categoryId, file })));

    if (!pending.length && !formData.filesToDelete.length) {
      return true;
    }

    // categoryIds lines up with files by index
    const body = new FormData();
    pending.forEach(({ categoryId, file }) => {
      body.append('files', file.pendingFile);
      body.append('categoryIds', categoryId);
    });
    formData.filesToDelete.forEach((file) => body.append('filesToDelete', String(file.id)));

    /** @type {OfferFile[] | undefined} */
    const uploadedFiles = await apiPost(`offers/${id}/versions/${version}/files`, body);

    // apiPost already reported the error
    if (!uploadedFiles) {
      return false;
    }

    if (uploadedFiles.length === pending.length) {
      // uploaded files come back in the order they were sent
      const uploaded = new Map(pending.map(({ file }, index) => [file.id, uploadedFiles[index]]));
      for (const categoryId of Object.keys(formData.files)) {
        formData.files[categoryId] = formData.files[categoryId].map((file) => uploaded.get(file.id) ?? file);
      }
    } else {
      // the response can't be matched to what was sent, so take the stored files as they are
      await getOfferFiles(id, version);
      for (const categoryId of Object.keys(formData.files)) {
        formData.files[categoryId] = offerFiles.value.filter((file) => String(file.categoryId) === categoryId);
      }
    }
    // revoke only once the form no longer shows the local previews
    pending.forEach(({ file }) => URL.revokeObjectURL(file.url));
    formData.filesToDelete = [];

    return true;
  }

  function reset() {
    offer.value = null;
    versions.value = null;
    offerFiles.value = [];
    offerFileCategories.value = [];
  }

  return {
    offer,
    versions,
    offerFiles,
    offerFileCategories,
    offerDocument,
    offerIsWithdrawn,
    getOfferByID,
    getOfferVersions,
    getOfferAttachments,
    signOffer,
    storeUpsell,
    submitCustomerView,
    submitWithdrawal,
    getOfferDocument,
    getOfferFiles,
    getOfferFileCategories,
    postOfferFiles,
  };
});
